import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc, getDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';

// ---------------------------------------------------------------------------
// Module-level helpers
// ---------------------------------------------------------------------------

/**
 * getUpgradeCost
 * Pure function — no dependency on state. Defined at module level
 * so it is never re-created on re-render.
 */
const getUpgradeCost = (currentLevel) => 100 + Math.pow(currentLevel, 2) * 10;

/**
 * getDateStrings
 * Returns today's and yesterday's locale date strings in one pass.
 * Extracted to avoid repeating the Date construction logic inline.
 */
const getDateStrings = () => {
  const today     = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return {
    todayStr:     today.toLocaleDateString('en-CA'),
    yesterdayStr: yesterday.toLocaleDateString('en-CA'),
  };
};

// ---------------------------------------------------------------------------

/**
 * usePlayerData
 *
 * Owns the player's economy: gold, level, inventory, and all
 * Firebase sync operations related to them.
 *
 * @param {object}   options.user              - Firebase user from useAuth.
 * @param {function} options.setStats          - Setter from useStats.
 * @param {function} options.setHasPlayedToday - Setter from useGameEngine.
 * @param {function} options.updateDirectory   - Function to update the fish directory.
 * @param {function} options.loadCloudDirectory - Function to load cloud directory data.
 */
export function usePlayerData({ user, setStats, setHasPlayedToday, updateDirectoryRef, loadCloudDirectoryRef  } = {}) {

 
  // --- Economy State ---
  const [gold,      setGold]      = useState(() => Number(localStorage.getItem('fishle-gold')) || 0);
  const [level,     setLevel]     = useState(0);
  const [inventory, setInventory] = useState(() => {
    try {
      const saved = localStorage.getItem('fishle-inventory');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [isProcessingBuy, setIsProcessingBuy] = useState(false);

  // Refs so async functions always read the latest user/level/gold
  // without needing to be re-created via useCallback when they change.
  const userRef  = useRef(user);
  const levelRef = useRef(level);
  const goldRef  = useRef(gold);

  // Keep refs in sync with latest values
  useEffect(() => { userRef.current  = user;  }, [user]);
  useEffect(() => { levelRef.current = level; }, [level]);
  useEffect(() => { goldRef.current  = gold;  }, [gold]);

  // Persist economy state to localStorage so it survives remounts
  useEffect(() => {
  localStorage.setItem('fishle-inventory', JSON.stringify(inventory));
  }, [inventory]);

  useEffect(() => {
  localStorage.setItem('fishle-gold', JSON.stringify(gold));
  }, [gold]);

  // --- Derived State ---
  // Sorted inventory is derived from inventory — no separate state needed.
  const sortedInventory = useMemo(() =>
    [...inventory].sort((a, b) => new Date(b.caughtAt) - new Date(a.caughtAt)),
    [inventory]
  );

  // ---------------------------------------------------------------------------
  // fetchUserData
  // ---------------------------------------------------------------------------

  const fetchUserData = useCallback(async (uid) => {
    try {
      const userDocRef  = doc(db, 'users', uid);
      const userSnap = await getDoc(userDocRef);
      if (!userSnap.exists()) return;

      const data                    = userSnap.data();
      const { todayStr, yesterdayStr } = getDateStrings();
      let currentStreak             = data.stats?.currentStreak ?? 0;
      const lastWin                 = data.stats?.lastWinDate;

      // Streak ender: missed more than one day → reset
      const missedStreak = lastWin && lastWin !== todayStr && lastWin !== yesterdayStr;
      if (missedStreak) {
        currentStreak = 0;
        await updateDoc(userDocRef, { 'stats.currentStreak': 0 });
      }

      // Sync cloud data into local state — only overwrite if field exists
      if (data.inventory !== undefined) {
        setInventory(data.inventory);
        setTimeout(() => {
          if (updateDirectoryRef?.current) {
            data.inventory.forEach(fish => updateDirectoryRef.current(fish));
          }
        }, 0);
      }
      // Load the player's saved Fish Directory from Firebase and merge it
      // into local state. Without this, the directory only ever reflects
      // what's been caught in the current session.
      if (data.fishDirectory !== undefined) {
        setTimeout(() => {
          loadCloudDirectoryRef?.current?.(data.fishDirectory);
        }, 0);
      }
      if (data.gold      !== undefined) setGold(data.gold);
      if (data.level     !== undefined) setLevel(data.level);
      if (data.stats     !== undefined) {
        setStats(prev => ({ ...prev, ...data.stats, currentStreak }));
      }

      // Gatekeeper: lock UI if already played today
      setHasPlayedToday?.(data.lastPlayedDate === todayStr);

    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  }, [setStats, setHasPlayedToday, updateDirectoryRef, loadCloudDirectoryRef]);

  // ---------------------------------------------------------------------------
  // resetPlayerData
  // ---------------------------------------------------------------------------

  const resetPlayerData = useCallback(() => {
    setInventory([]);
    setGold(0);
    setLevel(0);
  }, []);

  // ---------------------------------------------------------------------------
  // keepFish
  // ---------------------------------------------------------------------------

  const keepFish = useCallback(async (fish) => {
    if (!fish) return;

    // Generate caughtAt once — used for BOTH local state and Firebase
    // so they are always identical and arrayRemove can find an exact match
    const caughtAt = new Date().toISOString();

    const fishToStore = {
      id:         fish.id,
      name:       fish.name,
      weight:     fish.weight,
      effect:     fish.effect,
      rarity:     fish.rarity,
      sellPrice:  fish.sellPrice,
      stars:      fish.stars,
      instanceId: fish.instanceId,
      caughtAt,
    };

    // Local state and Firebase now have the exact same object
    setInventory(prev => [...prev, fishToStore]);

    const currentUser = userRef.current;
    if (!currentUser) return;

    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        inventory: arrayUnion(fishToStore),
      });
    } catch (error) {
      console.error('Error saving fish to cloud:', error);
    }
  }, []);


  // ---------------------------------------------------------------------------
  // handleSellFish
  // ---------------------------------------------------------------------------

  const handleSellFish = useCallback(async (fishToSell) => {
  if (!fishToSell) return;

  setInventory(prev => prev.filter(f => f.caughtAt !== fishToSell.caughtAt));
  setGold(prev => prev + fishToSell.sellPrice);

  const currentUser = userRef.current;
  if (!currentUser?.uid) return;

  // Strip any UI-only fields (_index, _total, etc.) before passing to arrayRemove.
  // Firestore requires an exact field-for-field match — extra fields cause silent failure.
  const cleanFish = {
    id:        fishToSell.id,
    name:      fishToSell.name,
    weight:    fishToSell.weight,
    effect:    fishToSell.effect,
    rarity:    fishToSell.rarity,
    sellPrice: fishToSell.sellPrice,
    stars:     fishToSell.stars,
    caughtAt:  fishToSell.caughtAt,
    // Only include instanceId if it actually exists on this fish —
    // matches the absence of the key in Firebase for older catches.
    ...(fishToSell.instanceId !== undefined && { instanceId: fishToSell.instanceId }),
  };

  try {
    await updateDoc(doc(db, 'users', currentUser.uid), {
      inventory: arrayRemove(cleanFish),
      gold:      increment(fishToSell.sellPrice),
    });
  } catch (error) {
    console.error('Error selling fish:', error);
  }
}, []);
  // ---------------------------------------------------------------------------
  // handlePurchaseLevel
  // ---------------------------------------------------------------------------

  const handlePurchaseLevel = useCallback(async () => {
    if (isProcessingBuy) return;

    const cost        = getUpgradeCost(levelRef.current);
    const currentUser = userRef.current;

    if (goldRef.current < cost) {
      alert('Not enough gold!');
      return;
    }

    setIsProcessingBuy(true);
    setGold(prev  => prev - cost);
    setLevel(prev => prev + 1);

    if (currentUser) {
      try {
        await updateDoc(doc(db, 'users', currentUser.uid), {
          gold:  increment(-cost),
          level: increment(1),
        });
      } catch (error) {
        console.error('Error upgrading level:', error);
      }
    }

    setIsProcessingBuy(false);
  }, [isProcessingBuy]);

  return {
    gold,
    setGold,
    level,
    setLevel,
    inventory,
    setInventory,
    sortedInventory,
    isProcessingBuy,
    getUpgradeCost,
    fetchUserData,
    resetPlayerData,
    keepFish,
    handleSellFish,
    handlePurchaseLevel,
  };
}